import { partialParse } from "openai-partial-json-parser";
import {
  AskAiChatMessageFragmentFragment,
  ChatbotRoleEnum,
  GetAskAiChatSessionHistoryQueryQuery,
  GetAskAiChatSessionQueryQuery,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import { assertEdgesNonNullWithStringId } from "@helpers/helpers";

export const askAIPlaceholderDebounceTime = 100;

export const createChatMessageNode = (
  partialChatMessage: Partial<AskAiChatMessageFragmentFragment>,
): AskAiChatMessageFragmentFragment => ({
  __typename: "ChatMessageNode",
  id: uuidv4(),
  role: ChatbotRoleEnum.User,
  content: "",
  imageUrls: [],

  error: false,
  autogenerated: false,
  isStreaming: false,

  createdArtifact: null,
  toolCalls: [],

  ...partialChatMessage,
});

export const parsePartialJson = (jsonStr: string) => {
  return partialParse(jsonStr);
};

const TAG_ATTR_RE = /([a-zA-Z_][\w-]*)="([^"]*)"/g;

const parseTagAttrs = (attrs: string): Record<string, string> => {
  const parsedAttrs: Record<string, string> = {};
  for (const match of attrs.matchAll(TAG_ATTR_RE)) {
    parsedAttrs[match[1]] = match[2];
  }
  return parsedAttrs;
};

const base64UrlEncodeJson = (data: Record<string, unknown>): string => {
  const bytes = new TextEncoder().encode(JSON.stringify(data));
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join(
    "",
  );
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const base64UrlDecodeJson = (value?: string): Record<string, any> => {
  if (!value) return {};
  try {
    const base64 = value
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(value.length / 4) * 4, "=");
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return {};
  }
};

export const createSuggestedAssessmentResponseTag = ({
  suggestedResponse,
  citations = [],
  suggestionId,
}: {
  suggestedResponse: string;
  citations?: any[];
  suggestionId?: string | null;
}): string =>
  `<tf-suggested-assessment-response data-props="${base64UrlEncodeJson({
    citations,
    suggestion_id: suggestionId || uuidv4(),
  })}">${suggestedResponse}</tf-suggested-assessment-response>`;

// --- extractTextFromContent ---

const extractTitlesFromLegacyItem = (item: any): string => {
  if (!item || typeof item !== "object") return "";
  if (item.type === "suggested_assessment_responses") {
    const firstResponse = item.items?.[0]?.suggested_response;
    return typeof firstResponse === "string" ? firstResponse : "";
  }
  const title = item.title || item.name;
  if (typeof title === "string") return `- ${title}\n`;
  const items = item.items;
  if (Array.isArray(items)) {
    return items
      .map((sub: any) => {
        const subTitle = sub?.title || sub?.name;
        return typeof subTitle === "string" ? `- ${subTitle}\n` : "";
      })
      .join("");
  }
  const overview = item.overview;
  if (typeof overview === "string") return overview + "\n";
  return "";
};

export const extractTextFromContent = (content: string): string => {
  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) return content;
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed?.output_items)) {
      return parsed.output_items
        .map((item: any) => {
          if (item?.type === "plain_text" && typeof item?.text === "string")
            return item.text;
          return extractTitlesFromLegacyItem(item);
        })
        .join("");
    }
    return "";
  } catch {
    return content;
  }
};

// --- splitAssessmentResponseTags ---

export type AssessmentResponseData = {
  suggestedResponse: string;
  citations: {
    title: string;
    url: string;
    sourceId: string;
    sourceProvider?: string | null;
    sourceType?: string | null;
  }[];
  suggestionId: string;
};

const SUGGESTED_ASSESSMENT_RESPONSE_TAG_RE =
  /<tf-suggested-assessment-response([^>]*)>([\s\S]*?)<\/tf-suggested-assessment-response>|<tf_suggested_assessment_response>([\s\S]*?)<\/tf_suggested_assessment_response>/g;
const OPEN_SUGGESTED_ASSESSMENT_RESPONSE_TAGS = [
  "<tf-suggested-assessment-response",
  "<tf_suggested_assessment_response>",
];
const CLOSE_SUGGESTED_ASSESSMENT_RESPONSE_TAGS = [
  "</tf-suggested-assessment-response>",
  "</tf_suggested_assessment_response>",
];

const normalizeCitation = (citation: any) => {
  if (typeof citation === "string") {
    return { title: citation, url: "", sourceId: citation };
  }
  return {
    title:
      citation?.title || citation?.source_title || citation?.sourceId || "",
    url: citation?.url || citation?.source_url || "",
    sourceId: citation?.sourceId || citation?.source_id || "",
    sourceProvider:
      citation?.sourceProvider || citation?.source_provider || null,
    sourceType: citation?.sourceType || citation?.source_type || null,
  };
};

export const splitAssessmentResponseTags = (
  text: string,
  { isStreaming = false }: { isStreaming?: boolean } = {},
): Array<string | AssessmentResponseData> => {
  const segments: Array<string | AssessmentResponseData> = [];
  let cursor = 0;

  for (const match of text.matchAll(SUGGESTED_ASSESSMENT_RESPONSE_TAG_RE)) {
    let before = text.slice(cursor, match.index);
    if (isStreaming && before) {
      const openTagIndexes = OPEN_SUGGESTED_ASSESSMENT_RESPONSE_TAGS.map(
        (tag) => before.indexOf(tag),
      ).filter((i) => i >= 0);
      const openTagIndex = openTagIndexes.length
        ? Math.min(...openTagIndexes)
        : -1;
      if (openTagIndex >= 0) before = before.slice(0, openTagIndex);
    }
    if (before) segments.push(before);

    try {
      if (match[2] !== undefined) {
        const attrs = parseTagAttrs(match[1] || "");
        const props = base64UrlDecodeJson(attrs["data-props"]);
        const suggestedResponse = match[2].trim();
        if (suggestedResponse) {
          segments.push({
            suggestedResponse,
            citations: Array.isArray(props.citations)
              ? props.citations.map(normalizeCitation)
              : [],
            suggestionId: props.suggestion_id || props.suggestionId || uuidv4(),
          });
        }
      } else {
        const data = JSON.parse(match[3]);
        const suggestedResponse =
          data?.suggested_response || data?.suggestedResponse || "";
        if (suggestedResponse) {
          segments.push({
            suggestedResponse,
            citations: Array.isArray(data?.citations)
              ? data.citations.map(normalizeCitation)
              : [],
            suggestionId: data?.suggestion_id || data?.suggestionId || uuidv4(),
          });
        }
      }
    } catch {
      segments.push(match[0]);
    }
    cursor = (match.index || 0) + match[0].length;
  }

  let remaining = text.slice(cursor);
  if (isStreaming) {
    const openTagIndexes = OPEN_SUGGESTED_ASSESSMENT_RESPONSE_TAGS.map((tag) =>
      remaining.indexOf(tag),
    ).filter((i) => i >= 0);
    const openTagIndex = openTagIndexes.length
      ? Math.min(...openTagIndexes)
      : -1;
    if (openTagIndex >= 0) remaining = remaining.slice(0, openTagIndex);
  }
  if (remaining) segments.push(remaining);

  return segments;
};

// --- getMessageCopyText ---

// Produces clean, paste-ready text for a chat message: flattens JSON
// output_items and strips the tf-suggested-assessment-response wrapper tags,
// keeping only the inner suggested-response text (what the user actually sees).
export const getMessageCopyText = (content: string): string => {
  const text = extractTextFromContent(content);
  return splitAssessmentResponseTags(text)
    .map((segment) =>
      typeof segment === "string" ? segment : segment.suggestedResponse,
    )
    .join("")
    .trim();
};

export const getStreamingAssessmentResponseText = (
  text: string,
): string | null => {
  const openTagIndexes = OPEN_SUGGESTED_ASSESSMENT_RESPONSE_TAGS.map((tag) =>
    text.lastIndexOf(tag),
  );
  const openTagIndex = Math.max(...openTagIndexes);
  if (openTagIndex < 0) return null;

  const hasClosingTagAfterOpen = CLOSE_SUGGESTED_ASSESSMENT_RESPONSE_TAGS.some(
    (tag) => text.indexOf(tag, openTagIndex) >= 0,
  );
  if (hasClosingTagAfterOpen) return null;

  const contentStart = text.indexOf(">", openTagIndex);
  if (contentStart < 0) return "";
  return text.slice(contentStart + 1).trim();
};

// --- getChatSessionTitle ---

export const getChatSessionTitle = (
  chatSession:
    | GetAskAiChatSessionQueryQuery["chatSession"]
    | NonNullable<
        NonNullable<
          GetAskAiChatSessionHistoryQueryQuery["chatSessions"]
        >["edges"][number]
      >["node"],
) => {
  if (chatSession?.assessmentQuestion?.title) {
    return `Context: ${chatSession.assessmentQuestion.title}`;
  }
  const messages = chatSession?.messages
    ? assertEdgesNonNullWithStringId(chatSession.messages)
    : [];
  const firstMessage = messages.find(
    (message) => message.role === ChatbotRoleEnum.User,
  );
  return (
    firstMessage?.content ||
    (firstMessage?.imageUrls.length ? "Image message" : undefined)
  );
};
